import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { DashboardShell } from "@/components/layout/DashboardShell";
import LoginPage from "@/pages/LoginPage";
import AuthCallbackPage from "@/pages/AuthCallbackPage";
import DashboardPage from "@/pages/DashboardPage";
import AccessionRegisterPage from "@/pages/AccessionRegisterPage";
import MembershipRegisterPage from "@/pages/MembershipRegisterPage";
import IssueRegisterPage from "@/pages/IssueRegisterPage";
import ReturnRegisterPage from "@/pages/ReturnRegisterPage";
import FineRegisterPage from "@/pages/FineRegisterPage";
import PeriodicalRegisterPage from "@/pages/PeriodicalRegisterPage";
import StockRegisterPage from "@/pages/StockRegisterPage";
import WeedingRegisterPage from "@/pages/WeedingRegisterPage";
import MasterDataPage from "@/pages/MasterDataPage";
import SettingsPage from "@/pages/SettingsPage";
import NotificationsPage from "@/pages/NotificationsPage";
import ReportsPage from "@/pages/ReportsPage";
import UserManagementPage from "@/pages/UserManagementPage";
import type { JSX } from "react";

interface ProtectedRouteProps {
  children: JSX.Element;
  roles?: string[];
}

function ProtectedRoute({ children, roles }: ProtectedRouteProps): JSX.Element {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center h-screen">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center h-screen text-lg font-medium">Loading...</div>;

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DashboardShell />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="accession" element={<AccessionRegisterPage />} />
        <Route path="membership" element={<MembershipRegisterPage />} />
        <Route path="issue" element={<IssueRegisterPage />} />
        <Route path="return" element={<ReturnRegisterPage />} />
        <Route path="fines" element={<FineRegisterPage />} />
        <Route path="periodicals" element={<PeriodicalRegisterPage />} />
        <Route path="stock" element={<StockRegisterPage />} />
        <Route path="weeding" element={<WeedingRegisterPage />} />
        <Route path="masters/:masterType" element={<MasterDataPage />} />
        <Route
          path="users"
          element={
            <ProtectedRoute roles={["admin"]}>
              <UserManagementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="settings"
          element={
            <ProtectedRoute roles={["admin"]}>
              <SettingsPage />
            </ProtectedRoute>
          }
        />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="reports" element={<ReportsPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  const routerBasename = process.env.PUBLIC_URL && process.env.PUBLIC_URL !== "/" ? process.env.PUBLIC_URL : undefined;

  return (
    <AuthProvider>
      <BrowserRouter basename={routerBasename}>
        <AppRoutes />
        <Toaster position="top-right" richColors />
        <div className="fixed bottom-2 right-3 z-40 pointer-events-none select-none text-[10px] sm:text-xs text-foreground/60">
          made with ❤️ 
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
